import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { LoanDetails, Product, RepaymentFrequency, RepaymentType } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency } from "../../../utils/namesFormater";
import { monthlyPeriodColumns, productTypeColumn } from "../../../shared/UIParts/columnsFormatter";
import { colorProductType } from "../../../utils/colorEnumFormatter";
import { formatProductType } from "../../../utils/enumFormatter";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { MimshakService } from "../../../services/mimshakService";


interface Props {
  products: Product[];
}



const LoanRepaymentTable: React.FC<Props> = ({ products }) => {
   const loansByProduct = useMemo(() => {
  const deduped = MimshakService.getDeduplicatedLoansByProduct(products);
  const result: Record<string, { schedule: Record<number, number>; product_type: number }> = {};

  Object.entries(deduped).forEach(([key, loans]) => {
    const merged: Record<number, number> = {};

    loans.forEach(({ loan, policy }) => {
      const schedule = MimshakGroupByService.getLoanRepaymentSchedule([loan], policy.update_date);
      Object.entries(schedule).forEach(([period, amount]) => {
        merged[+period] = (merged[+period] || 0) + amount;
      });
    });

    const product = products[Number(key.replace("p-", ""))];
    result[key] = {
      schedule: merged,
      product_type: product.details.product_type,
    };
  });

  return result;
}, [products]);

  
    const allPeriods = useMemo(() => {
      const periods = new Set<number>();
      Object.values(loansByProduct).forEach(row => {
        Object.keys(row.schedule).forEach(period => periods.add(Number(period)));
      });
      return Array.from(periods).sort((a, b) => a - b);
    }, [loansByProduct]);
  
    const rows = useMemo(() => {
      return Object.entries(loansByProduct).map(([id, data], idx) => {
        const row: any = { id: `p-${idx}`, product_type: data.product_type, label: formatProductType(data.product_type) };
        allPeriods.forEach(period => {
          row[period] = data.schedule[period] || 0;
        });
        return row;
      });
    }, [loansByProduct, allPeriods]);
  
    const totalRow = useMemo(() => {
      const row: any = { id: "total", product_type: -1, label: "סה\"כ" };
      allPeriods.forEach(period => {
        row[period] = rows.reduce((acc, curr) => acc + (curr[period] || 0), 0);
      });
      return row;
    }, [rows, allPeriods]);
  
    const columns: GridColDef[] = useMemo(() => {
      const base: GridColDef[] = [
        {
          field: "label",
          headerName: "סוג מוצר",
          width: 180,
          renderCell: (params) => {
            const isTotal = params.row.id === "total";
            return (
              <Box
                sx={{
                  backgroundColor: isTotal ? "#1976d2" : colorProductType(params.row.product_type),
                  color: "white",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  width: "100%",
                  textAlign: "center",
                  fontWeight: isTotal ? "bold" : "normal",
                }}
              >
                {params.value}
              </Box>
            );
          },
        },
      ];
      const periodCols = monthlyPeriodColumns(allPeriods)
    //   const periodCols = allPeriods.map(period => ({
    //     field: period.toString(),
    //     headerName: `${String(period).slice(0, 4)}-${String(period).slice(4, 6)}`,
    //     width: 120,
    //     align: "center" as const,
    //     headerAlign: "center" as const,
    //     renderCell: (params: any) => params.value > 0 ? formatCurrency(params.value) : "",
    //   }));
      return [...base, ...periodCols];
    }, [allPeriods]);
  
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          טבלת החזרי הלוואות לפי מוצר
        </Typography>
        <StyledDataGrid
          rows={[...rows, totalRow]}
          columns={columns}
          hideFooter
          autoHeight
        />
      </Box>
    );
  };
  
  export default LoanRepaymentTable;
  